// Sprint 20.8 (D013-EC-76) — Runner usage modal: real Anthropic data
// per runner.
//
// Pre-S20.8 the modal projected the runner-side `cap_5h_residual_pct`
// / `weekly_hours_*` heartbeat fields populated by
// `runner/health/cap-tracker.ts`. Those values measure "how many times
// the daemon spawned the `claude` binary in 5 h" — explicitly NOT
// authoritative (per the cap-tracker module header). The modal showed
// 1 % while Claude Code in VS Code reported 30 % for the same Max
// account; the data was effectively invented from the founder's POV.
//
// This rewrite replaces the projection with the five canonical buckets
// the Anthropic OAuth `/api/oauth/usage` endpoint returns, fetched on
// the runner side (where the OAuth tokens live post-D-013) and
// surfaced through the `RunnerUsageBridge` fan-out. Each runner row
// renders its own bars; on failure the bars are replaced by a
// founder-mode error sentence (token expired, runner offline, rate
// limited, etc.) — never an empty pill.
//
// Cursor parity (`cursor-ux-mirror.md` §2.4): same shadcn/radix Dialog
// shell, same Spinner / Icon helpers, no new pattern invented.

import { useEffect } from 'react';
// DS.7 D17 — `ds/DsModal` -> `@ds/overlays/Modal` at `size="lg"`. Two fences
// cross here and both are RECORDED, not deleted on a hunch: the hand-rolled
// `max-h-[80vh] flex flex-col overflow-hidden` shell and the `-mx-6 px-6`
// scroll panel were added by sprint `usage-limits-v2` D6 *because "the
// DialogContent primitive has no max-height by default"*. The DS `Modal` sets
// `max-height: calc(100vh - 128px)` on its content box and ships a dedicated
// scrolling `.body` slot between a pinned header and a pinned footer
// (Modal.module.css:21-31), so the fence's stated reason ceases to apply and
// the classes go with it. The retry row moves to the `toolbar` slot — pinned
// between the header and the scrolling body, which is exactly what its own
// comment asked for ("sits below the header rather than inside it so it never
// collides with the close button").
//
// ACCEPTED DIFF (D9): 672 px (`sm:max-w-2xl`) -> 800 px. `Modal`'s sizes are
// 480/560/800/full and it exposes no `width`; `size="lg"` is the nearest. The
// generic escape is an upstream `width` prop (§6 row 6), never a local fork.
import { DsModal } from './ds/DsModal';
import { Spinner } from './atoms/Spinner';
import { Icon } from './atoms/Icon';
import { Badge } from './atoms/Badge';
import Button from '@ds/actions/Button';
import { useTranslation } from '../i18n';
import type {
  RunnerUsageEntry,
  RunnerUsageErrorCode,
  UsageBucket,
  UsageData,
  UsageLimitEntry,
} from '../lib/api';
import { formatRelativeTime } from '../lib/time';
import { semverLt, fleetMaxVersion } from '../lib/semver';
import { deriveRunnerLabel } from './RunnerChip';
import { useRunnersUsageStore } from '../stores/runners-usage.store';
import {
  UsageAgeCaption,
  isUsageReadingStale,
  isFleetAgeCaptionSuppressed,
  oldestUsageAtIso,
  usageAgeMs,
  rpcBucketsUsable,
  coarseAge,
} from './atoms/UsageAgeCaption';
import { useRunnersStore } from '../stores/runners.store';
import {
  classifyRunner,
  type RunnerAvailabilityState,
} from '../lib/runner-availability';

export interface UsageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Test/story injection: when set, seeds the store immediately.
  initialRunners?: RunnerUsageEntry[];
}

type ToastTKey = Parameters<ReturnType<typeof useTranslation>['t']>[0];

// Sprint runner-quarantine-truth (D4, D013-EC-126) — STATUS renders the
// shared availability classification, not raw WS `status`. The raw
// projection said "ONLINE / LAST SEEN now" about quarantined runners
// while the topbar banner said "All servers offline" about the same
// fleet. Quarantined and draining are warning-class (degraded, not
// gone); offline keeps the muted treatment.
const AVAILABILITY_BADGE_COLOR: Record<
  RunnerAvailabilityState,
  'success' | 'error' | 'warning' | 'muted'
> = {
  available: 'success',
  offline: 'muted',
  draining: 'warning',
  quarantined: 'warning',
};

const AVAILABILITY_LABEL_KEY: Record<RunnerAvailabilityState, ToastTKey> = {
  available: 'usage.statusOnline',
  offline: 'usage.statusOffline',
  draining: 'usage.statusDraining',
  quarantined: 'usage.statusQuarantined',
};

// Founder-mode copy keys for each per-runner failure mode. Translated
// inline by `t(...)` so a row that comes back with `usageErrorCode`
// renders a plain-language sentence instead of a stuck "—" or "0 %".
const ERROR_LABEL_KEY: Record<RunnerUsageErrorCode, ToastTKey> = {
  OAUTH_TOKEN_MISSING: 'usage.errorOauthMissing',
  OAUTH_TOKEN_INVALID: 'usage.errorOauthInvalid',
  RATE_LIMITED: 'usage.errorRateLimited',
  UPSTREAM_ERROR: 'usage.errorUpstream',
  TIMEOUT: 'usage.errorTimeout',
  RUNNER_OFFLINE: 'usage.errorRunnerOffline',
  BRIDGE_STOPPED: 'usage.errorUpstream',
  BRIDGE_UNCONFIGURED: 'usage.errorUpstream',
};

// INV-X: modal reads from the same `runners-usage.store` that the
// pill reads from. On open, triggers a fresh hydrate so the data is
// current; between opens the polling keeps the cache warm.
export function UsageModal({ open, onOpenChange, initialRunners }: UsageModalProps) {
  const { t } = useTranslation();
  const entriesByRunnerId = useRunnersUsageStore((s) => s.entriesByRunnerId);
  const loaded = useRunnersUsageStore((s) => s.loaded);
  const error = useRunnersUsageStore((s) => s.error);
  const hydrate = useRunnersUsageStore((s) => s.hydrate);
  const _setEntries = useRunnersUsageStore((s) => s._setEntries);

  useEffect(() => {
    if (!open) return;
    if (initialRunners) {
      _setEntries(initialRunners);
      return;
    }
    // Sprint usage-last-known-good (D013-EC-172) D2 — WITHDRAWS the
    // `bypassBackoff` this call site carried since usage-backoff-truth
    // (D013-EC-148 D1). That flag made opening the modal dispatch an
    // upstream RPC even while a fresh Retry-After window was armed, which
    // inside a limited fleet guaranteed a 429 → the cache's error path →
    // the numbers the founder was looking at replaced by an amber card.
    // Opening the panel was what emptied it.
    //
    // The constraint that flag served (Santi cannot tell "Anthropic is
    // still limiting us" from "we are simply not asking") is a
    // RENDERING-ambiguity constraint, and it is now answered at the
    // rendering layer by D4's `lastAttempt` line — without spending an
    // upstream call. Ground truth stays one click away via "Retry now".
    // Full fence note: spec §2.
    void hydrate({ fresh: true });
  }, [open, initialRunners, hydrate, _setEntries]);

  const runners = Object.values(entriesByRunnerId);

  return (
    <DsModal
      open={open}
      onClose={() => onOpenChange(false)}
      size="lg"
      title={t('usage.title')}
      description={t('usage.description')}
      data-testid="usage-modal"
      toolbar={
        /* Sprint usage-last-known-good (D013-EC-172) D2 + pre-mortem F3 —
           the deliberate escape hatch, and the ONLY path left that talks
           to Anthropic on a founder's click. It is rendered
           unconditionally (it used to live inside the error branch, where
           it was unreachable in exactly the state that sprint creates: a
           cached row served with no error at all). The DS `toolbar` slot is
           its home: pinned between the header and the scrolling body, so it
           can neither collide with the close button nor scroll away. */
        <div className="flex items-center justify-end gap-2">
          <span className="text-caption text-muted-foreground">
            {t('usage.retryNowHint')}
          </span>
          <Button
            variant="outlined"
            size="sm"
            onClick={() => void hydrate({ fresh: true, bypassBackoff: true })}
            data-testid="usage-retry"
          >
            {t('usage.retryNow')}
          </Button>
        </div>
      }
    >
        <div data-testid="usage-scroll">

        {!loaded && !error && (
          <div
            className="flex items-center justify-center gap-2 py-8 text-muted-foreground"
            data-testid="usage-loading"
          >
            <Spinner size="md" />
            <span className="text-body">{t('usage.loading')}</span>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="usage-error">
            <Icon name="AlertCircle" size={24} className="text-status-error" />
            <p className="text-body text-muted-foreground">{error}</p>
            {/* The retry control moved to the persistent header row above
                (D2) — one control, always reachable, one vocabulary. */}
          </div>
        )}

        {loaded && !error && runners.length === 0 && (
          <p
            className="text-caption text-muted-foreground rounded-md border border-border/50 bg-card p-3"
            data-testid="usage-empty"
          >
            {t('usage.empty')}
          </p>
        )}

        {loaded && !error && runners.length > 0 && (
          <UsageBody runners={runners} />
        )}
        </div>
    </DsModal>
  );
}

function UsageBody({ runners }: { runners: RunnerUsageEntry[] }) {
  const { t } = useTranslation();
  const earliest = pickEarliestReset(runners);
  // Sprint runner-claude-version-visibility — the drift reference is the
  // fleet maximum (spec §6 MVP; npm-latest is a documented follow-up).
  // Computed once here and passed down so a row can badge itself
  // "outdated" without each row re-scanning the fleet.
  const fleetMaxClaudeVersion = fleetMaxVersion(runners.map((r) => r.claudeCliVersion));
  // Sprint usage-last-known-good (D013-EC-172) D3 — was the store's
  // entry-level `fetchedAtIso` (when the cache last reached Anthropic for
  // the FLEET). After D1 that clock can be minutes old while a given
  // runner's numbers are hours old, so the footer now reports the oldest
  // reading actually on screen.
  const atIso = oldestUsageAtIso(runners);

  return (
    <div className="space-y-3" data-testid="usage-body">
      <ul className="space-y-2" data-testid="usage-runner-list">
        {runners.map((runner) => (
          <RunnerUsageRow
            key={runner.runnerId}
            runner={runner}
            fleetMaxClaudeVersion={fleetMaxClaudeVersion}
          />
        ))}
      </ul>

      <div
        className="border-t border-border/50 pt-3 text-caption text-muted-foreground"
        data-testid="usage-footer"
      >
        {earliest ? (
          t('usage.footerEarliestReset', {
            hostname: earliest.hostname,
            time: formatResetCountdown(earliest.resetIso),
          })
        ) : (
          t('usage.footerNoReset')
        )}
        {/* Separator lives INSIDE the conditional: suppressing only the
            caption left a trailing "· " on the line (Gate 2, note 1). */}
        {!isFleetAgeCaptionSuppressed(runners) && (
          <>
            {' · '}
            <UsageAgeCaption atIso={atIso} data-testid="usage-modal-age" />
          </>
        )}
      </div>
    </div>
  );
}

function RunnerUsageRow({
  runner,
  fleetMaxClaudeVersion,
}: {
  runner: RunnerUsageEntry;
  fleetMaxClaudeVersion: string | null;
}) {
  const { t } = useTranslation();
  // `RunnerUsageEntry` does not carry `unavailableReason` (it is a
  // usage-bridge row), so quarantine visibility comes from joining the
  // runners store snapshot by id. When the snapshot has not hydrated
  // yet (test/story `initialRunners` injection, first paint) we
  // classify from the entry's own fields — reason-less, so the row
  // degrades to the pre-sprint online/draining/offline projection
  // rather than guessing.
  const snapshot = useRunnersStore((s) => s.runnersById[runner.runnerId]);
  const availability = classifyRunner(
    snapshot ?? {
      status: runner.status,
      drainMode: runner.drainMode,
      unavailableReason: null,
    },
  );
  const statusColor = AVAILABILITY_BADGE_COLOR[availability.state];
  const statusLabel = t(AVAILABILITY_LABEL_KEY[availability.state]);
  const label = deriveRunnerLabel(runner.runnerId);
  // Sprint usage-last-known-good (D013-EC-172) — load-bearing, and the
  // reason `RunnerLastAttemptNote` MUST NOT read from here: after D1 this
  // yields `undefined` for a carried-forward row, which is exactly HOW the
  // amber card disappears. Sourcing D4's muted line from this variable
  // would render nothing in precisely the state that needs it (spec §4,
  // consumer census, `UsageModal.tsx:251`).
  const errorCode = runner.usage === null ? runner.usageErrorCode : undefined;
  const stale = isUsageReadingStale(runner.usageAtIso);

  return (
    <li
      className="rounded-md border border-border/50 bg-card p-3 space-y-3"
      data-testid="usage-row"
      data-runner-id={runner.runnerId}
      data-runner-state={availability.state}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Badge
            variant="dot"
            color={statusColor}
            className="w-2.5 h-2.5 shrink-0"
            aria-label={statusLabel}
          />
          <div className="flex-1 min-w-0">
            <div className="text-body text-foreground font-550 truncate">
              {label}
            </div>
            <div className="text-caption text-muted-foreground truncate">
              {runner.hostname}
            </div>
            {/* Sprint runner-claude-version-visibility — the runner's
                Claude Code version, with a subtle "outdated" badge only
                when it is strictly behind the fleet maximum. A runner that
                has not reported (NULL) shows a neutral "—", never a false
                warning (spec AC-3/AC-4). */}
            <div
              className="flex items-center gap-1.5 mt-0.5"
              data-testid="usage-claude-version"
            >
              <span className="text-caption text-muted-foreground truncate">
                {t('usage.claudeVersionLabel')} {runner.claudeCliVersion ?? '—'}
              </span>
              {semverLt(runner.claudeCliVersion, fleetMaxClaudeVersion) && (
                <Badge
                  variant="count"
                  color="warning"
                  className="shrink-0 text-label uppercase"
                  data-testid="usage-version-outdated"
                >
                  {t('usage.versionOutdated')}
                </Badge>
              )}
            </div>
          </div>
          <Badge variant="count" color={statusColor} className="shrink-0 text-label uppercase">
            {statusLabel}
          </Badge>
        </div>
      </div>

      {/* Sprint usage-last-known-good (D013-EC-172) D3 — priority order:
          (1) the numbers, whenever `usage` exists, captioned with THEIR
          own age; (2) beneath them, when the latest attempt did not
          succeed, one muted line — never an amber card that replaces the
          bars; (3) the amber card ONLY for a row that has never had a
          successful reading. With a carried-forward row this branch now
          takes the bars path, which IS the desired outcome.

          D5: past 60 min the row LEADS with the failure line and the
          numbers render muted, so a runner dead for hours cannot keep
          looking healthy (pre-mortem F2). */}
      {runner.usage ? (
        <div className="space-y-2" data-testid="usage-row-numbers">
          {stale && <AttemptOrStreamNote runner={runner} />}
          <div className={stale ? 'opacity-50' : undefined}>
            <UsageBars entry={runner} />
          </div>
          {/* Gate 2 (2026-08-12): render the caption only when there is an
              age to state. A row carrying numbers with no stamp comes from
              a backend older than this sprint (deviation 3), and
              `ageUnknown` would caption real bars "No usage reading yet" —
              a contradiction on the same line, and exactly the founder-
              first §8 failure the caption exists to prevent. */}
          {usageAgeMs(runner.usageAtIso) !== null && (
            <UsageAgeCaption
              atIso={runner.usageAtIso}
              data-testid="usage-row-age"
            />
          )}
          {!stale && <AttemptOrStreamNote runner={runner} />}
        </div>
      ) : (
        <RunnerUsageError
          errorCode={errorCode ?? 'UPSTREAM_ERROR'}
          hostname={runner.hostname}
          lastSeen={runner.lastSeen}
          retryAtIso={runner.usageRetryAtIso}
        />
      )}

      <dl className="grid grid-cols-3 gap-x-4 gap-y-1 text-caption pt-1 border-t border-border/30">
        <CompactStat
          label={t('usage.colAccount')}
          value={runner.claudeAccountId ?? t('usage.capUnknown')}
        />
        <CompactStat
          label={t('usage.colStatus')}
          value={statusLabel}
        />
        <CompactStat
          label={t('usage.colLastSeen')}
          value={runner.lastSeen ? formatRelativeTime(runner.lastSeen) : '—'}
        />
      </dl>
    </li>
  );
}

// Sprint usage-limits-v2 (2026-07-08): renders the canonical `limits[]`
// array when present (v10 wire). Falls back to the legacy per-bucket
// projection (D2) when `limits` is absent — a v9 runner, or a v10
// runner whose Anthropic response predates the array shape.
//
// Sprint usage-from-stream (D3b iii, D6) — takes the whole `entry` now
// (not just `usage`): `limits[]` is RPC-only forever (spec §1), so it
// renders ONLY while `rpcBucketsUsable(entry)` — otherwise a stream-only
// or stale-RPC row falls straight to `LegacyBars`, restricted to the two
// stream-eligible windows (five_hour/seven_day). Two live bars beat
// three where one is dead.
function UsageBars({ entry }: { entry: RunnerUsageEntry }) {
  const usage = entry.usage;
  if (!usage) return null;
  const usable = rpcBucketsUsable(entry);
  if (usable && usage.limits && usage.limits.length > 0) {
    return <LimitsBars limits={usage.limits} extraUsage={usage.extra_usage} />;
  }
  return <LegacyBars entry={entry} usage={usage} limitsUsable={usable} />;
}

function LimitsBars({
  limits,
  extraUsage,
}: {
  limits: UsageLimitEntry[];
  extraUsage: UsageData['extra_usage'];
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {limits.map((entry, i) => (
        <LimitEntryBar
          key={`${entry.kind}-${entry.modelName ?? entry.group ?? i}`}
          entry={entry}
          testId={`usage-limit-${entry.kind}${entry.modelName ? `-${entry.modelName}` : ''}`}
        />
      ))}
      {extraUsage && (
        <UsageBar
          label={t('usage.extraUsage')}
          bucket={extraUsage}
          subLabelOverride={extraUsageSubLabel(extraUsage)}
          testId="usage-bar-extra-usage"
        />
      )}
    </div>
  );
}

// Sprint usage-from-stream (D3) — reads the per-window provenance stamp
// (`windowSources[bucket].atIso`, falling back to the row-level
// `usageAtIso` for an implicit-rpc bucket — same fallback D3b documents
// everywhere else) so `UsageBar`'s passed-reset copy can state "última
// hace X" for the two stream-eligible windows.
function bucketReadingAtIso(
  entry: RunnerUsageEntry,
  bucket: 'five_hour' | 'seven_day' | 'seven_day_overage_included',
): string | null {
  return entry.windowSources?.[bucket]?.atIso ?? entry.usageAtIso ?? null;
}

function LegacyBars({
  entry,
  usage,
  limitsUsable,
}: {
  entry: RunnerUsageEntry;
  usage: UsageData;
  // Sprint usage-from-stream (D6) — `seven_day_sonnet`/`seven_day_opus`/
  // `extra_usage` are RPC-only forever; suppressed here when the row's
  // own RPC reading isn't usable, even if a stale value survives on the
  // carried-forward object (D3b iii force, extended to the legacy path).
  limitsUsable: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {usage.five_hour && (
        <UsageBar
          label={t('usage.session5h')}
          bucket={usage.five_hour}
          testId="usage-bar-five-hour"
          readingAtIso={bucketReadingAtIso(entry, 'five_hour')}
        />
      )}
      {usage.seven_day && (
        <UsageBar
          label={t('usage.weekly7d')}
          bucket={usage.seven_day}
          testId="usage-bar-seven-day"
          readingAtIso={bucketReadingAtIso(entry, 'seven_day')}
        />
      )}
      {/* Sprint usage-fable-window-from-stream (D4 i) — stream-sourced,
          NOT gated by `limitsUsable`: this bucket only ever comes from
          the stream (D3), so an unusable RPC has no bearing on it. */}
      {usage.seven_day_overage_included && (
        <UsageBar
          label={t('usage.weeklyFable')}
          bucket={usage.seven_day_overage_included}
          testId="usage-bar-seven-day-fable"
          readingAtIso={bucketReadingAtIso(entry, 'seven_day_overage_included')}
        />
      )}
      {limitsUsable && usage.seven_day_sonnet && (
        <UsageBar
          label={t('usage.weeklySonnet')}
          bucket={usage.seven_day_sonnet}
          testId="usage-bar-seven-day-sonnet"
        />
      )}
      {limitsUsable && usage.seven_day_opus && (
        <UsageBar
          label={t('usage.weeklyOpus')}
          bucket={usage.seven_day_opus}
          testId="usage-bar-seven-day-opus"
        />
      )}
      {limitsUsable && usage.extra_usage && (
        <UsageBar
          label={t('usage.extraUsage')}
          bucket={usage.extra_usage}
          subLabelOverride={extraUsageSubLabel(usage.extra_usage)}
          testId="usage-bar-extra-usage"
        />
      )}
    </div>
  );
}

// Founder-facing label for a canonical `limits[]` kind. Kinds we don't
// recognise fall back to a humanised version of the raw string
// ("weekly_scoped" → "Weekly scoped") so a new limit kind from
// Anthropic still renders sensibly on the next runner deploy.
function labelForLimit(entry: UsageLimitEntry, t: ReturnType<typeof useTranslation>['t']): string {
  if (entry.kind === 'session') return t('usage.limitSession');
  if (entry.kind === 'weekly_all') return t('usage.limitWeeklyAll');
  if (entry.kind === 'weekly_scoped' && entry.modelName) {
    return t('usage.limitWeeklyScopedModel', { model: entry.modelName });
  }
  if (entry.kind === 'weekly_scoped') return t('usage.limitWeeklyScoped');
  // Fallback: turn "future_kind_2027" into "Future kind 2027".
  const humanised = entry.kind.replace(/_/g, ' ');
  return humanised.charAt(0).toUpperCase() + humanised.slice(1);
}

function LimitEntryBar({
  entry,
  testId,
}: {
  entry: UsageLimitEntry;
  testId: string;
}) {
  const { t } = useTranslation();
  const label = labelForLimit(entry, t);
  // Clamp at render (D4 — schema does NOT clamp; Anthropic can report
  // >100 % when the last request pushed the account past the ceiling).
  const displayPercent = Math.max(0, Math.min(100, entry.percent));
  // "Limit reached" state: either the entry declares itself active OR
  // the reported percent has already reached 100 %. Renders red +
  // "Limit reached — resets in Xh Ym" instead of the neutral countdown.
  const limitReached = entry.isActive === true || entry.percent >= 100;

  const barColor = limitReached
    ? 'bg-status-error'
    : displayPercent >= 90
      ? 'bg-status-error'
      : displayPercent >= 70
        ? 'bg-status-warning'
        : 'bg-status-success';

  const countdown = formatResetCountdown(entry.resets_at);
  const subLabel = limitReached
    ? t('usage.limitReached', { time: countdown })
    : t('usage.resetsIn', { time: countdown });

  return (
    <div
      className="space-y-1"
      data-testid={testId}
      data-limit-kind={entry.kind}
      data-limit-active={limitReached ? 'true' : 'false'}
    >
      <div className="flex items-center justify-between text-caption">
        <span className={limitReached ? 'text-status-error font-550' : 'text-foreground'}>
          {label}
        </span>
        <span
          className={
            limitReached
              ? 'text-status-error tabular-nums font-550'
              : 'text-muted-foreground tabular-nums'
          }
        >
          {t('usage.percent', { value: Math.round(entry.percent) })}
        </span>
      </div>
      <div
        className="h-2 w-full rounded-full bg-muted overflow-hidden"
        role="progressbar"
        aria-valuenow={displayPercent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full rounded-full transition-all duration-300 ${barColor}`}
          style={{ width: `${displayPercent}%` }}
        />
      </div>
      <p
        className={
          limitReached
            ? 'text-label text-status-error'
            : 'text-label text-muted-foreground/80'
        }
      >
        {subLabel}
      </p>
    </div>
  );
}

function extraUsageSubLabel(
  bucket: UsageBucket & { spent_usd?: number; limit_usd?: number },
): string | null {
  if (typeof bucket.spent_usd === 'number' && typeof bucket.limit_usd === 'number') {
    return `$${bucket.spent_usd.toFixed(2)} / $${bucket.limit_usd.toFixed(2)}`;
  }
  return null;
}

// Sprint usage-last-known-good (D013-EC-172) D4 — the muted line that
// names WHICH silence this is. This is what retires the real constraint
// behind the withdrawn `bypassBackoff` (spec §2): the founder can now tell
// "Anthropic is still limiting us" from "we are simply not asking" by
// reading, not by spending an upstream call.
//
// It reads `lastAttempt` + `runner.usageErrorCode` DIRECTLY. It must never
// be fed `RunnerUsageRow`'s `errorCode` local, which is `undefined` for
// every carried-forward row by design.
//
// Renders nothing when the latest attempt succeeded, and nothing on a
// pre-D1 backend that omits `lastAttempt` (absent ≠ failed).
export function RunnerLastAttemptNote({ runner }: { runner: RunnerUsageEntry }) {
  const { t } = useTranslation();
  const lastAttempt = runner.lastAttempt;
  if (lastAttempt === undefined || lastAttempt === 'ok') return null;

  const retryAtIso = runner.usageRetryAtIso;
  const retryAtLabel =
    retryAtIso && Number.isFinite(Date.parse(retryAtIso))
      ? new Date(retryAtIso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : null;

  const message =
    retryAtLabel === null
      ? // Upstream never told us when to come back (timeout, auth, parse).
        // Naming an instant we do not have would be a fabrication.
        t('usage.lastAttemptFailed')
      : lastAttempt === 'not_asked'
        ? t('usage.lastAttemptNotAsked', { time: retryAtLabel })
        : t('usage.lastAttemptFailedRetryAt', { time: retryAtLabel });

  return (
    <p
      className="text-caption text-muted-foreground"
      data-testid="usage-row-last-attempt"
      data-last-attempt={lastAttempt}
      data-error-code={runner.usageErrorCode ?? ''}
    >
      {message}
    </p>
  );
}

// Sprint usage-from-stream (D3) — true when at least one of the two
// stream-eligible windows was won by a stream reading (D3's merge
// precedence). Distinguishes "this row is genuinely fed by the CLI
// stream" from "this row's RPC merely failed with no stream data at
// all" — a plain pre-sprint RPC-only row must keep showing
// `RunnerLastAttemptNote` exactly as before (it has no stream fallback
// to point to).
function isStreamBacked(runner: RunnerUsageEntry): boolean {
  const sources = runner.windowSources;
  return (
    sources?.five_hour?.source === 'stream' ||
    sources?.seven_day?.source === 'stream' ||
    sources?.seven_day_overage_included?.source === 'stream'
  );
}

// Sprint usage-from-stream (D4, change 9b) — the muted line that
// replaces `RunnerLastAttemptNote` for a genuinely stream-backed row:
// numbers ARE showing (`runner.usage` is non-null — D3's merge, or the
// modal would be in the `RunnerUsageError` branch instead), the row's
// OWN RPC reading is not usable (`!rpcBucketsUsable`), AND a stream
// reading is what is actually keeping the numbers alive
// (`isStreamBacked`) — the amber error card vanished by construction
// the moment `usage` became non-null (`UsageModal.tsx` errorCode local,
// unchanged), so a dead RPC would otherwise leave NO visible trace at
// all until EC-222's alert ships. `RunnerLastAttemptNote`'s own inputs
// are UNTOUCHED (fence F10) — this decides WHICH note to render, at the
// call site, never inside it.
function AttemptOrStreamNote({ runner }: { runner: RunnerUsageEntry }) {
  const { t } = useTranslation();
  if (runner.usage !== null && !rpcBucketsUsable(runner) && isStreamBacked(runner)) {
    return (
      <p
        className="text-caption text-muted-foreground"
        data-testid="usage-row-stream-only-note"
      >
        {t('usage.streamOnlyNote')}
      </p>
    );
  }
  return <RunnerLastAttemptNote runner={runner} />;
}

function RunnerUsageError({
  errorCode,
  hostname,
  lastSeen,
  retryAtIso,
}: {
  errorCode: RunnerUsageErrorCode;
  hostname: string;
  lastSeen: string | null;
  // Sprint usage-backoff-truth (D013-EC-148) D2 — set by the backend on
  // rate-limited rows only.
  retryAtIso?: string;
}) {
  const { t } = useTranslation();
  // D013-EC-148 — "Anthropic rate-limited the usage endpoint. Try again
  // in a few minutes" left the founder guessing how many minutes and
  // whether anything was retrying at all. When the backend tells us the
  // instant it will ask again, say it.
  const retryAtLabel =
    errorCode === 'RATE_LIMITED' && retryAtIso && Number.isFinite(Date.parse(retryAtIso))
      ? new Date(retryAtIso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : null;
  const message =
    errorCode === 'RUNNER_OFFLINE'
      ? t('usage.errorRunnerOffline', {
          lastSeen: lastSeen ? formatRelativeTime(lastSeen) : '—',
        })
      : retryAtLabel
        ? t('usage.errorRateLimitedRetryAt', { hostname, time: retryAtLabel })
        : t(ERROR_LABEL_KEY[errorCode], { hostname });
  return (
    <p
      className="text-caption text-status-warning rounded-md bg-status-warning/10 px-3 py-2"
      data-testid="usage-row-error"
      data-error-code={errorCode}
    >
      {message}
    </p>
  );
}

// Per-bucket progress bar driven by the bucket's `utilization` field.
// Bar height steps up from `h-1.5` (6 px) to `h-2` (8 px) on Tailwind's
// standard spacing scale so a low single-digit utilisation like 17 %
// reads as a visible green slab rather than a hairline. Token
// discipline (design-discipline.md): only use scale values, never
// arbitrary `h-[Npx]`. The colour thresholds mirror the legacy modal:
// green default, amber at 70 %, red at 90 %.
function UsageBar({
  label,
  bucket,
  subLabelOverride,
  testId,
  readingAtIso,
}: {
  label: string;
  bucket: UsageBucket;
  subLabelOverride?: string | null;
  testId: string;
  // Sprint usage-from-stream (D4) — when this bucket's window has
  // ROTATED (`resets_at` in the past) and no new reading has arrived
  // since, the bar renders EMPTY with `usage.noReadingSinceReset`
  // instead of a numeric percent that can only ever OVERSTATE usage
  // (the founder's stated fear, spec §1). `readingAtIso` — this
  // bucket's own reading stamp, when known — feeds the "última hace X"
  // clause; omitted for buckets with no per-window provenance (RPC-only
  // forever: sonnet/opus/extra_usage/limits).
  readingAtIso?: string | null;
}) {
  const { t } = useTranslation();
  const resetMs = Date.parse(bucket.resets_at);
  const passedReset = Number.isFinite(resetMs) && resetMs < Date.now();
  if (passedReset) {
    const ageMs = usageAgeMs(readingAtIso);
    const age = ageMs !== null ? coarseAge(ageMs) : t('usage.ageUnknownShort');
    return (
      <div className="space-y-1" data-testid={testId} data-usage-bar-state="no-reading-since-reset">
        <div className="flex items-center justify-between text-caption">
          <span className="text-foreground">{label}</span>
        </div>
        <div
          className="h-2 w-full rounded-full bg-muted overflow-hidden"
          role="progressbar"
          // Reviewer fix round 1, issue 4 — no `aria-valuenow`: the
          // reading is genuinely UNKNOWN (D4's render-only "unknown"
          // state), not zero. Per WAI-ARIA, an indeterminate progressbar
          // OMITS `aria-valuenow` entirely rather than asserting a false
          // 0 — the same "never a numeric 0%" guarantee this bar's whole
          // branch exists to uphold, extended to assistive tech.
          aria-valuemin={0}
          aria-valuemax={100}
        />
        <p className="text-label text-muted-foreground/80">
          {t('usage.noReadingSinceReset', { age })}
        </p>
      </div>
    );
  }
  const clamped = Math.max(0, Math.min(100, bucket.utilization));
  // Sprint usage-fable-window-from-stream (D4 i-b) — limit-reached state,
  // EVERY bucket this component renders (not just the new Fable bar —
  // `usage-bar-seven-day` gets it too, same semantics `LimitEntryBar`
  // already has for `limits[]` entries). Checked AFTER the passed-reset
  // branch above, which keeps precedence (F5 — no bar that was red turns
  // green; this state is ADDED above the existing 70/90 thresholds).
  const limitReached = bucket.utilization >= 100;
  const barColor = limitReached
    ? 'bg-status-error'
    : clamped >= 90
      ? 'bg-status-error'
      : clamped >= 70
        ? 'bg-status-warning'
        : 'bg-status-success';

  const countdown = formatResetCountdown(bucket.resets_at);
  const subLabel = limitReached
    ? (subLabelOverride ?? t('usage.limitReached', { time: countdown }))
    : (subLabelOverride ?? t('usage.resetsIn', { time: countdown }));

  return (
    <div
      className="space-y-1"
      data-testid={testId}
      data-usage-bar-state={limitReached ? 'limit-reached' : undefined}
    >
      <div className="flex items-center justify-between text-caption">
        <span className={limitReached ? 'text-status-error font-550' : 'text-foreground'}>
          {label}
        </span>
        <span className="text-muted-foreground tabular-nums">
          {t('usage.percent', { value: Math.round(clamped) })}
        </span>
      </div>
      <div
        className="h-2 w-full rounded-full bg-muted overflow-hidden"
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full rounded-full transition-all duration-300 ${barColor}`}
          style={{ width: `${clamped}%` }}
        />
      </div>
      {subLabel && (
        <p className="text-label text-muted-foreground/80">{subLabel}</p>
      )}
    </div>
  );
}

function CompactStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col min-w-0">
      <dt className="text-label uppercase text-muted-foreground">{label}</dt>
      <dd className="text-caption text-foreground truncate">{value}</dd>
    </div>
  );
}

interface EarliestReset {
  hostname: string;
  resetIso: string;
}

// Picks the runner whose soonest future bucket reset comes first
// across ALL five buckets (not just five_hour as the pre-S20.8 modal
// did). Past timestamps are ignored — a stale resets_at from a runner
// whose response was cached by Anthropic isn't a "next reset". Returns
// null when no runner has any future reset.
//
// Sprint usage-from-stream (D3b iii, change 9a) — `limits[]` (and the
// three RPC-only-forever buckets in the legacy fallback) are read ONLY
// while `rpcBucketsUsable(r)`; an hours-old `limits[]` reset must not
// win over a genuinely-soon stream-fed five_hour/seven_day reset just
// because it was read FIRST. five_hour/seven_day stay unconditional —
// they are exactly the two windows the stream keeps live.
export function pickEarliestReset(
  runners: RunnerUsageEntry[],
  nowMs: number = Date.now(),
): EarliestReset | null {
  let bestHost: string | null = null;
  let bestIso: string | null = null;
  let bestMs = Number.POSITIVE_INFINITY;
  for (const r of runners) {
    if (!r.usage) continue;
    const usable = rpcBucketsUsable(r, nowMs);
    // Prefer the canonical `limits[]` resets when available (v10) AND
    // usable; otherwise fall back to the legacy per-bucket resets (D2),
    // itself restricted to the stream-eligible windows when not usable.
    const resetIsoList: string[] = [];
    if (usable && r.usage.limits && r.usage.limits.length > 0) {
      for (const entry of r.usage.limits) resetIsoList.push(entry.resets_at);
      if (r.usage.extra_usage) resetIsoList.push(r.usage.extra_usage.resets_at);
    } else {
      // Sprint usage-fable-window-from-stream (D4 vi) — the Fable bucket
      // is unconditional, like five_hour/seven_day (it is stream-fed,
      // never gated by `usable`).
      const legacyBuckets = usable
        ? [
            r.usage.five_hour,
            r.usage.seven_day,
            r.usage.seven_day_overage_included,
            r.usage.seven_day_sonnet,
            r.usage.seven_day_opus,
            r.usage.extra_usage,
          ]
        : [r.usage.five_hour, r.usage.seven_day, r.usage.seven_day_overage_included];
      for (const bucket of legacyBuckets) {
        if (bucket) resetIsoList.push(bucket.resets_at);
      }
    }
    for (const iso of resetIsoList) {
      const ms = Date.parse(iso);
      if (Number.isNaN(ms) || ms <= nowMs) continue;
      if (ms < bestMs) {
        bestMs = ms;
        bestHost = r.hostname;
        bestIso = iso;
      }
    }
  }
  if (bestHost === null || bestIso === null) return null;
  return { hostname: bestHost, resetIso: bestIso };
}

function formatResetCountdown(iso: string, nowMs: number = Date.now()): string {
  const diffMs = Math.max(0, Date.parse(iso) - nowMs);
  const totalMinutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return '<1m';
}
